// Training service for managing LoRA training jobs
import { doc, getDoc, setDoc, updateDoc, onSnapshot, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import apiService from './apiService';

/**
 * Start a new training job using backend API
 * @param {string} userId - User ID
 * @param {string} sessionId - Upload session ID
 * @returns {Promise<Object>} - Training job response
 */
export const startTrainingJob = async (userId, sessionId) => {
    try {
        // Call backend API to start training
        const response = await apiService.startTraining(sessionId);
        
        console.log('✅ Training job started:', response);
        return response;
    } catch (error) {
        console.error('Error starting training job:', error);
        throw new Error(error.message || 'Failed to start training job');
    }
};

/**
 * Get training job details
 * @param {string} jobId - Training job ID
 * @returns {Promise<Object|null>} - Training job data
 */
export const getTrainingJob = async (jobId) => {
    try {
        const jobDoc = await getDoc(doc(db, 'trainingJobs', jobId));
        
        if (!jobDoc.exists()) {
            return null;
        }
        
        return {
            id: jobDoc.id,
            ...jobDoc.data()
        };
    } catch (error) {
        console.error('Error fetching training job:', error);
        return null;
    }
};

/**
 * Get user's current training job from Firestore directly
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} - Current training job or null
 */
export const getCurrentTrainingJob = async (userId) => {
    try {
        console.log('🔍 Checking Firestore for training jobs...');
        
        // Get the most recent training job for this user directly from Firestore
        const trainingJobsQuery = query(
            collection(db, 'trainingJobs'),
            where('userId', '==', userId),
            orderBy('createdAt', 'desc'),
            limit(1)
        );
        
        const querySnapshot = await getDocs(trainingJobsQuery);
        
        if (querySnapshot.empty) {
            console.log('❌ No training jobs found in Firestore');
            return null;
        }
        
        const trainingJob = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
        };
        
        console.log('📄 Training job found:', trainingJob);
        
        // If job is still in progress, call backend to get latest status from Replicate
        if (['starting', 'queued', 'training'].includes(trainingJob.status) && trainingJob.replicateJobId) {
            console.log('🔄 Job in progress, checking latest status from backend...');
            try {
                const backendStatus = await apiService.getTrainingStatus(userId);
                return backendStatus;
            } catch (backendError) {
                console.warn('Backend status check failed, using Firestore data:', backendError.message);
                return trainingJob;
            }
        }
        
        // Job is completed/failed, return Firestore data
        return trainingJob;
        
    } catch (error) {
        console.error('Error fetching current training job:', error);
        return null;
    }
};

/**
 * Subscribe to training job updates
 * @param {string} jobId - Training job ID
 * @param {Function} callback - Callback function for updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToTrainingJob = (jobId, callback) => {
    return onSnapshot(
        doc(db, 'trainingJobs', jobId),
        (doc) => {
            if (doc.exists()) {
                callback({
                    id: doc.id,
                    ...doc.data()
                });
            } else {
                callback(null);
            }
        },
        (error) => {
            console.error('Error listening to training job:', error);
            callback(null);
        }
    );
};

/**
 * Update training job progress (typically called by backend/Cloud Functions)
 * @param {string} jobId - Training job ID
 * @param {Object} updates - Updates to apply
 */
export const updateTrainingProgress = async (jobId, updates) => {
    try {
        await updateDoc(doc(db, 'trainingJobs', jobId), {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating training progress:', error);
        throw error;
    }
};

/**
 * Cancel a training job
 * @param {string} jobId - Training job ID
 * @param {string} userId - User ID
 */
export const cancelTrainingJob = async (jobId, userId) => {
    try {
        await updateDoc(doc(db, 'trainingJobs', jobId), {
            status: 'cancelled',
            completedAt: new Date(),
            error: 'Cancelled by user'
        });
        
        await updateDoc(doc(db, 'users', userId), {
            currentTrainingJobId: null,
            trainingStatus: 'cancelled'
        });
    } catch (error) {
        console.error('Error cancelling training job:', error);
        throw error;
    }
};

/**
 * Retry a failed training job
 * @param {string} userId - User ID
 * @param {string} sessionId - Upload session ID
 * @returns {Promise<string>} - New training job ID
 */
export const retryTrainingJob = async (userId, sessionId) => {
    // Create a new training job
    return await startTrainingJob(userId, sessionId);
};